import { Chess } from 'chess.js';
import {
  generateChess960Fen,
  isKingOfTheHillWin,
  isPlayableVariant
} from './variants.js';

const DAY_MS = 24 * 60 * 60 * 1000;

export const TIME_CONTROLS = {
  '1+0': { label: 'Bullet 1+0', initialMs: 60000, incrementMs: 0, class: 'bullet', clockMode: 'fischer' },
  '2+1': { label: 'Bullet 2+1', initialMs: 120000, incrementMs: 1000, class: 'bullet', clockMode: 'fischer' },
  '3+0': { label: 'Blitz 3+0', initialMs: 180000, incrementMs: 0, class: 'blitz', clockMode: 'fischer' },
  '3+2': { label: 'Blitz 3+2', initialMs: 180000, incrementMs: 2000, class: 'blitz', clockMode: 'fischer' },
  '5+0': { label: 'Blitz 5+0', initialMs: 300000, incrementMs: 0, class: 'blitz', clockMode: 'fischer' },
  '5+3': { label: 'Blitz 5+3', initialMs: 300000, incrementMs: 3000, class: 'blitz', clockMode: 'fischer' },
  '10+0': { label: 'Rapid 10+0', initialMs: 600000, incrementMs: 0, class: 'rapid', clockMode: 'fischer' },
  '15+10': { label: 'Rapid 15+10', initialMs: 900000, incrementMs: 10000, class: 'rapid', clockMode: 'fischer' },
  '25+10': { label: 'Rapid 25+10', initialMs: 1500000, incrementMs: 10000, class: 'rapid', clockMode: 'fischer' },
  '90+30': { label: 'Classical 90+30', initialMs: 5400000, incrementMs: 30000, class: 'classical', clockMode: 'fischer' },
  '60+0': { label: 'Classical 60+0', initialMs: 3600000, incrementMs: 0, class: 'classical', clockMode: 'fischer' },
  '90+0': { label: 'Classical 90+0', initialMs: 5400000, incrementMs: 0, class: 'classical', clockMode: 'fischer' },
  unlimited: {
    label: 'Unlimited',
    display: '∞',
    class: 'untimed',
    clockMode: 'none',
    incrementMs: 0
  },
  '1d/move': { label: 'Daily 1 day/move', perMoveMs: DAY_MS, class: 'daily', clockMode: 'perMove' },
  '3d/move': { label: 'Daily 3 days/move', perMoveMs: 3 * DAY_MS, class: 'daily', clockMode: 'perMove' }
};

export const TIME_CLASSES = ['bullet', 'blitz', 'rapid', 'classical', 'untimed', 'daily'];

const DEFAULT_TIME_CONTROL_BY_CLASS = {
  bullet: '1+0',
  blitz: '3+0',
  rapid: '10+0',
  classical: '90+30',
  untimed: 'unlimited',
  daily: '1d/move'
};

export const END_REASONS = [
  'checkmate',
  'resign',
  'flag',
  'draw-agreed',
  'stalemate',
  'repetition',
  '50-move',
  'insufficient',
  'abort',
  'abandoned',
  'join-timeout',
  'forfeit',
  'no-show',
  'king-of-the-hill',
  'three-check'
];

export const DEFAULT_JOIN_WAIT_MS = 15 * 60 * 1000;
export const MATCH_FORFEIT_GRACE_MS = 5 * 60 * 1000;

export const MOVE_GRACE_MS = 300;
export const MAX_TOTAL_GRACE_MS = 10000;

function requireTimeControl(key) {
  const control = TIME_CONTROLS[key];
  if (!control) {
    throw new Error(`Unsupported time control: ${key}`);
  }
  return control;
}

export function timeControlsForClass(timeClass) {
  return Object.entries(TIME_CONTROLS)
    .filter(([, control]) => control.class === timeClass)
    .map(([key]) => key);
}

export function defaultTimeControlForClass(timeClass) {
  const preferred = DEFAULT_TIME_CONTROL_BY_CLASS[timeClass];
  if (preferred && TIME_CONTROLS[preferred]?.class === timeClass) {
    return preferred;
  }
  const controls = timeControlsForClass(timeClass);
  if (controls.length === 0) {
    throw new Error(`Unsupported time class: ${timeClass}`);
  }
  return controls[0];
}

function startingClockMs(control) {
  if (control.clockMode === 'none') {
    return null;
  }
  return control.perMoveMs ?? control.initialMs;
}

export function isUntimedControl(timeControl) {
  return TIME_CONTROLS[timeControl]?.clockMode === 'none';
}

export function allowsRatedPlay(timeControl) {
  return Boolean(TIME_CONTROLS[timeControl] && !isUntimedControl(timeControl));
}

function resolveInitialFen({ variant = 'standard', initialFen }) {
  if (initialFen) {
    return initialFen;
  }
  if (variant === 'chess960') {
    return generateChess960Fen();
  }
  return undefined;
}

function opposite(color) {
  return color === 'white' ? 'black' : 'white';
}

function chessColor(color) {
  return color === 'white' ? 'w' : 'b';
}

function playerColor(game, playerId) {
  if (game.players.white?.playerId === playerId) {
    return 'white';
  }
  if (game.players.black?.playerId === playerId) {
    return 'black';
  }
  return null;
}

export function chessFromGame(game, { withHistory = false } = {}) {
  // Prefer the current fen. Replaying the transcript every ply is quadratic and
  // stalls midgame on mobile. Threefold repetition is checked separately from
  // stored fenAfter keys — chess.js history is not required for applyMove.
  if (
    !withHistory
    && typeof game?.fen === 'string'
    && game.fen.length > 0
  ) {
    try {
      return new Chess(game.fen);
    } catch {
      // Rebuild from the signed move list below.
    }
  }
  const chess = new Chess(game.initialFen);
  for (const move of game.moves || []) {
    chess.move({
      from: move.from,
      to: move.to,
      promotion: move.promotion || undefined
    });
  }
  return chess;
}

/** FIDE repetition key: board, side to move, castling, and en passant only. */
export function positionKeyFromFen(fen) {
  if (typeof fen !== 'string' || !fen) {
    return '';
  }
  return fen.trim().split(/\s+/).slice(0, 4).join(' ');
}

/** Count how often a position appears in the initial fen plus each fenAfter. */
export function countPositionRepetitions(game, fen) {
  const key = positionKeyFromFen(fen);
  if (!key) {
    return 0;
  }
  let count = 0;
  if (positionKeyFromFen(game?.initialFen) === key) {
    count += 1;
  }
  for (const move of game?.moves || []) {
    if (positionKeyFromFen(move?.fenAfter) === key) {
      count += 1;
    }
  }
  return count;
}

/** Legal destinations from an already-loaded chess.js position (UI hot path). */
export function legalMovesFromPosition(chess, square) {
  if (!chess || !square) {
    return [];
  }
  return chess.moves({ square, verbose: true }).map((move) => ({
    from: move.from,
    to: move.to,
    promotion: move.promotion || null,
    san: move.san
  }));
}

export function createGame({
  id,
  white,
  black = null,
  timeControl = '3+0',
  now = Date.now(),
  rated = false,
  variant = 'standard',
  initialFen,
  joinWaitMs = DEFAULT_JOIN_WAIT_MS,
  scheduledStartAt = null,
  startMode = 'immediate',
  forfeitGraceMs = MATCH_FORFEIT_GRACE_MS,
  /** Set when challenging a specific player (chat / online / leaderboard). */
  challengedPlayerId = null
}) {
  if (!id || !white?.playerId) {
    throw new Error('A game ID and white player are required');
  }
  if (!isPlayableVariant(variant)) {
    throw new Error(`Unsupported variant: ${variant}`);
  }

  const control = requireTimeControl(timeControl);
  const chess = new Chess(resolveInitialFen({ variant, initialFen }));
  const clockMs = startingClockMs(control);

  return {
    schemaVersion: 1,
    id,
    revision: 0,
    rated: Boolean(rated) && allowsRatedPlay(timeControl),
    variant,
    timeControl,
    players: { white, black },
    status: black ? 'playing' : 'waiting',
    initialFen: chess.fen(),
    fen: chess.fen(),
    turn: chess.turn() === 'b' ? 'black' : 'white',
    moves: [],
    checks: { white: 0, black: 0 },
    clocks: {
      white: clockMs,
      black: clockMs
    },
    graceUsed: {
      white: 0,
      black: 0
    },
    activeSince: black ? now : null,
    startReady: black ? { white: true, black: true } : null,
    drawOffer: null,
    result: null,
    createdAt: now,
    startedAt: black ? now : null,
    joinDeadlineAt: black ? null : now + joinWaitMs,
    scheduledStartAt: scheduledStartAt || null,
    startMode: scheduledStartAt ? startMode : 'immediate',
    forfeitGraceMs: scheduledStartAt ? forfeitGraceMs : null,
    challengedPlayerId: challengedPlayerId || null,
    updatedAt: now
  };
}

export function joinGame(game, black, now = Date.now()) {
  if (game.status !== 'waiting' || game.players.black) {
    throw new Error('Game is no longer open');
  }
  if (!black?.playerId || black.playerId === game.players.white.playerId) {
    throw new Error('A distinct black player is required');
  }
  if (game.joinDeadlineAt && now > game.joinDeadlineAt) {
    throw new Error('Join window has expired');
  }

  return {
    ...game,
    revision: game.revision + 1,
    players: { ...game.players, black },
    status: 'starting',
    activeSince: null,
    startedAt: null,
    startReady: { white: false, black: false },
    startConfirmedAt: { white: null, black: null },
    updatedAt: now
  };
}

export function markReadyToStart(game, playerId, now = Date.now()) {
  if (game.status !== 'starting') {
    throw new Error('Game is not waiting to start');
  }

  const color = playerColor(game, playerId);
  if (!color) {
    throw new Error('Player is not seated in this game');
  }

  if (game.startReady?.[color]) {
    return game;
  }

  const startReady = {
    white: false,
    black: false,
    ...(game.startReady || {}),
    [color]: true
  };
  const startConfirmedAt = {
    white: null,
    black: null,
    ...(game.startConfirmedAt || {}),
    [color]: Number.isFinite(game.startConfirmedAt?.[color])
      ? game.startConfirmedAt[color]
      : now
  };
  const bothReady = startReady.white && startReady.black;

  if (!bothReady) {
    return {
      ...game,
      revision: game.revision + 1,
      startReady,
      startConfirmedAt,
      status: 'starting',
      updatedAt: now
    };
  }

  return beginGameFromReady(game, { startReady, startConfirmedAt }, now);
}

function finiteOrNull(value) {
  return Number.isFinite(value) ? value : null;
}

function mergedStartConfirmedAt(preferred, current, incoming) {
  return {
    white: finiteOrNull(
      preferred?.startConfirmedAt?.white
      ?? current?.startConfirmedAt?.white
      ?? incoming?.startConfirmedAt?.white
    ),
    black: finiteOrNull(
      preferred?.startConfirmedAt?.black
      ?? current?.startConfirmedAt?.black
      ?? incoming?.startConfirmedAt?.black
    )
  };
}

/**
 * Clock start must be identical on both boards. Concurrent ready-start actions
 * carry different `at` values; the contract seed is the later confirmation,
 * not whichever action each peer happened to apply second and not local merge time.
 */
export function alignContractStart(preferred, current, incoming) {
  if (!preferred || preferred.scheduledStartAt) {
    return preferred;
  }
  if (preferred.status !== 'playing' && preferred.status !== 'finished') {
    return preferred;
  }

  const startConfirmedAt = mergedStartConfirmedAt(preferred, current, incoming);
  const pairKnown = startConfirmedAt.white != null && startConfirmedAt.black != null;
  let startedAt = preferred.startedAt;
  if (pairKnown) {
    startedAt = Math.max(startConfirmedAt.white, startConfirmedAt.black);
  } else {
    const candidates = [preferred.startedAt, current?.startedAt, incoming?.startedAt]
      .filter((value) => Number.isFinite(value));
    if (candidates.length < 2) {
      return preferred;
    }
    startedAt = Math.max(...candidates);
  }

  const confirmedSame = preferred.startConfirmedAt?.white === startConfirmedAt.white
    && preferred.startConfirmedAt?.black === startConfirmedAt.black;
  if (startedAt === preferred.startedAt && (confirmedSame || !pairKnown)) {
    return preferred;
  }

  const noMoves = !(preferred.moves?.length);
  return {
    ...preferred,
    startConfirmedAt: pairKnown ? startConfirmedAt : preferred.startConfirmedAt,
    startedAt,
    activeSince: noMoves ? startedAt : preferred.activeSince
  };
}

/**
 * Choose / reconcile game replicas. Concurrent ready-start forks often share a
 * revision with different startReady bits — never clobber a local confirmation.
 */
export function preferGameState(current, incoming, now = Date.now()) {
  return alignContractStart(
    selectPreferredGameState(current, incoming, now),
    current,
    incoming
  );
}

function selectPreferredGameState(current, incoming, now = Date.now()) {
  if (!incoming) {
    return current || null;
  }
  if (!current) {
    return incoming;
  }
  if (incoming.id && current.id && incoming.id !== current.id) {
    return current;
  }
  if (incoming.revision > current.revision) {
    if (current.status === 'starting' && incoming.status === 'starting') {
      const startReady = {
        white: Boolean(current.startReady?.white || incoming.startReady?.white),
        black: Boolean(current.startReady?.black || incoming.startReady?.black)
      };
      if (startReady.white && startReady.black) {
        return beginGameFromReady(
          { ...incoming, startReady: { white: false, black: false } },
          { startReady },
          now
        );
      }
      return {
        ...incoming,
        startReady,
        updatedAt: Math.max(current.updatedAt || 0, incoming.updatedAt || 0, now)
      };
    }
    return incoming;
  }
  if (incoming.revision < current.revision) {
    if (current.status === 'starting' && incoming.status === 'starting') {
      const startReady = {
        white: Boolean(current.startReady?.white || incoming.startReady?.white),
        black: Boolean(current.startReady?.black || incoming.startReady?.black)
      };
      if (
        startReady.white === Boolean(current.startReady?.white)
        && startReady.black === Boolean(current.startReady?.black)
      ) {
        return current;
      }
      if (startReady.white && startReady.black) {
        return beginGameFromReady(current, { startReady }, now);
      }
      return {
        ...current,
        startReady,
        updatedAt: Math.max(current.updatedAt || 0, incoming.updatedAt || 0, now)
      };
    }
    if (incoming.status === 'playing' && current.status === 'starting') {
      return incoming;
    }
    return current;
  }

  if (current.status === 'playing' && incoming.status !== 'playing') {
    return current;
  }
  if (incoming.status === 'playing' && current.status !== 'playing') {
    return incoming;
  }
  if (current.status === 'finished' && incoming.status !== 'finished') {
    return current;
  }
  if (incoming.status === 'finished' && current.status !== 'finished') {
    return incoming;
  }

  if (current.status === 'starting' && incoming.status === 'starting') {
    const startReady = {
      white: Boolean(current.startReady?.white || incoming.startReady?.white),
      black: Boolean(current.startReady?.black || incoming.startReady?.black)
    };
    const unchanged = startReady.white === Boolean(current.startReady?.white)
      && startReady.black === Boolean(current.startReady?.black);
    if (unchanged) {
      return current;
    }
    if (startReady.white && startReady.black) {
      return beginGameFromReady(current, { startReady }, now);
    }
    return {
      ...current,
      startReady,
      updatedAt: Math.max(current.updatedAt || 0, incoming.updatedAt || 0, now)
    };
  }

  // Same revision: never let a newer timestamp clobber a longer transcript.
  const currentMoves = current.moves?.length || 0;
  const incomingMoves = incoming.moves?.length || 0;
  if (currentMoves !== incomingMoves) {
    return currentMoves > incomingMoves ? current : incoming;
  }

  return (current.updatedAt || 0) >= (incoming.updatedAt || 0) ? current : incoming;
}

export function forfeitDeadlineAt(game) {
  if (!game?.scheduledStartAt) {
    return null;
  }
  return game.scheduledStartAt + (game.forfeitGraceMs ?? MATCH_FORFEIT_GRACE_MS);
}

export function beginGameFromReady(game, nextReadyState, now = Date.now()) {
  const startReady = nextReadyState.startReady || game.startReady || {};
  if (!startReady.white || !startReady.black) {
    throw new Error('Both players must confirm before the clock starts');
  }
  if (game.scheduledStartAt && now < game.scheduledStartAt) {
    return {
      ...game,
      ...nextReadyState,
      revision: game.revision + 1,
      startReady,
      status: 'starting',
      activeSince: null,
      startedAt: null,
      updatedAt: now
    };
  }

  const startConfirmedAt = nextReadyState.startConfirmedAt || game.startConfirmedAt || null;
  const confirmedStart = startConfirmedAt?.white != null && startConfirmedAt?.black != null
    ? Math.max(startConfirmedAt.white, startConfirmedAt.black)
    : now;
  const clockStart = game.scheduledStartAt ?? confirmedStart;
  return {
    ...game,
    ...nextReadyState,
    revision: game.revision + 1,
    startReady,
    startConfirmedAt,
    status: 'playing',
    activeSince: clockStart,
    startedAt: clockStart,
    updatedAt: now
  };
}

export function maybeBeginScheduledGame(game, now = Date.now()) {
  if (game.status !== 'starting' || !game.scheduledStartAt || now < game.scheduledStartAt) {
    return game;
  }
  const startReady = game.startReady || {};
  if (!startReady.white || !startReady.black) {
    return game;
  }
  return beginGameFromReady(game, game, now);
}

export function canClaimStartForfeit(game, playerId, now = Date.now()) {
  if (game.status !== 'starting' || !game.scheduledStartAt || now < game.scheduledStartAt) {
    return false;
  }
  const color = playerColor(game, playerId);
  if (!color) {
    return false;
  }
  const opponent = opposite(color);
  if (game.startReady?.[opponent]) {
    return false;
  }
  return Boolean(game.startReady?.[color]);
}

export function claimStartForfeit(game, playerId, now = Date.now()) {
  if (!canClaimStartForfeit(game, playerId, now)) {
    throw new Error('Forfeit cannot be claimed yet');
  }
  const color = playerColor(game, playerId);
  return finish(
    { ...game, revision: game.revision + 1 },
    color === 'white' ? '1-0' : '0-1',
    'forfeit',
    now
  );
}

export function autoForfeitAbsentAtStart(game, now = Date.now()) {
  if (game.status !== 'starting' || !game.scheduledStartAt) {
    return game;
  }
  const deadline = forfeitDeadlineAt(game);
  if (!deadline || now < deadline) {
    return maybeBeginScheduledGame(game, now);
  }

  const startReady = game.startReady || {};
  if (startReady.white && startReady.black) {
    return beginGameFromReady(game, game, now);
  }
  if (startReady.white && !startReady.black) {
    return finish({ ...game, revision: game.revision + 1 }, '1-0', 'forfeit', now);
  }
  if (!startReady.white && startReady.black) {
    return finish({ ...game, revision: game.revision + 1 }, '0-1', 'forfeit', now);
  }
  return finish({ ...game, revision: game.revision + 1 }, null, 'no-show', now);
}

export function liveClocks(game, now = Date.now()) {
  if (game.status !== 'playing' || game.activeSince == null || isUntimedControl(game.timeControl)) {
    return { ...game.clocks };
  }

  const elapsed = Math.max(0, now - game.activeSince);
  return {
    ...game.clocks,
    [game.turn]: Math.max(0, game.clocks[game.turn] - elapsed)
  };
}

function adjudicatedClock(game, color, now) {
  const elapsed = Math.max(0, now - game.activeSince);
  const usedGrace = game.graceUsed?.[color] || 0;
  const graceRemaining = Math.max(0, MAX_TOTAL_GRACE_MS - usedGrace);
  const grace = Math.min(MOVE_GRACE_MS, graceRemaining, elapsed);
  return {
    remainingMs: Math.max(0, game.clocks[color] - (elapsed - grace)),
    grace
  };
}

function finish(game, result, endReason, now) {
  return {
    ...game,
    status: endReason === 'abort' || endReason === 'join-timeout' || endReason === 'no-show'
      ? 'aborted'
      : 'finished',
    activeSince: null,
    drawOffer: null,
    result: {
      value: result,
      endReason,
      endedAt: now
    },
    updatedAt: now
  };
}

function automaticEnding(chess, moverColor, game) {
  if (game.variant === 'kingOfTheHill' && isKingOfTheHillWin(chess, moverColor)) {
    return {
      value: moverColor === 'white' ? '1-0' : '0-1',
      endReason: 'king-of-the-hill'
    };
  }
  if (game.variant === 'threeCheck' && (game.checks?.[moverColor] || 0) >= 3) {
    return {
      value: moverColor === 'white' ? '1-0' : '0-1',
      endReason: 'three-check'
    };
  }
  if (chess.isCheckmate()) {
    return { value: moverColor === 'white' ? '1-0' : '0-1', endReason: 'checkmate' };
  }
  if (chess.isStalemate()) {
    return { value: '½-½', endReason: 'stalemate' };
  }
  if (countPositionRepetitions(game, game.fen || chess.fen()) >= 3) {
    return { value: '½-½', endReason: 'repetition' };
  }
  if (chess.isDrawByFiftyMoves()) {
    return { value: '½-½', endReason: '50-move' };
  }
  if (chess.isInsufficientMaterial()) {
    return { value: '½-½', endReason: 'insufficient' };
  }
  return null;
}

export function applyMove(game, { playerId, from, to, promotion = 'q' }, now = Date.now()) {
  if (game.status !== 'playing') {
    throw new Error('Game is not in progress');
  }

  const color = playerColor(game, playerId);
  if (!color) {
    throw new Error('Player is not seated in this game');
  }
  if (color !== game.turn) {
    throw new Error('It is not this player’s turn');
  }

  const control = requireTimeControl(game.timeControl);
  let clocksBeforeMove = { ...game.clocks };
  let graceUsedThisMove = 0;
  if (control.clockMode !== 'none') {
    const adjudicated = adjudicatedClock(game, color, now);
    graceUsedThisMove = adjudicated.grace;
    clocksBeforeMove = {
      ...game.clocks,
      [color]: adjudicated.remainingMs
    };
    if (adjudicated.remainingMs <= 0) {
      return finish(
        { ...game, clocks: clocksBeforeMove },
        color === 'white' ? '0-1' : '1-0',
        'flag',
        now
      );
    }
  }

  const chess = chessFromGame(game);
  const move = chess.move({ from, to, promotion });
  if (!move) {
    throw new Error('Illegal move');
  }

  const opponent = opposite(color);
  const nextClocks = { ...clocksBeforeMove };
  if (control.clockMode === 'perMove') {
    nextClocks[opponent] = control.perMoveMs;
  } else if (control.clockMode === 'fischer') {
    nextClocks[color] = clocksBeforeMove[color] + control.incrementMs;
  }
  const checks = { ...(game.checks || { white: 0, black: 0 }) };
  if (game.variant === 'threeCheck' && chess.inCheck()) {
    checks[color] += 1;
  }
  const moveRecord = {
    ply: game.moves.length + 1,
    color,
    from: move.from,
    to: move.to,
    promotion: move.promotion || null,
    san: move.san,
    fenAfter: chess.fen(),
    clockAfterMs: nextClocks[color],
    playedAt: now
  };
  const next = {
    ...game,
    revision: game.revision + 1,
    fen: chess.fen(),
    turn: opponent,
    moves: [...game.moves, moveRecord],
    clocks: nextClocks,
    checks,
    graceUsed: {
      ...(game.graceUsed || { white: 0, black: 0 }),
      [color]: (game.graceUsed?.[color] || 0) + graceUsedThisMove
    },
    activeSince: now,
    drawOffer: null,
    updatedAt: now
  };
  const ending = automaticEnding(chess, color, next);

  return ending ? finish(next, ending.value, ending.endReason, now) : next;
}

export function expireWaitingGame(game, now = Date.now()) {
  if (game.status !== 'waiting') {
    return game;
  }
  if (!game.joinDeadlineAt || now <= game.joinDeadlineAt) {
    return game;
  }
  return finish(
    { ...game, revision: game.revision + 1 },
    null,
    'join-timeout',
    now
  );
}

export function isJoinWindowOpen(game, now = Date.now()) {
  return game.status === 'waiting'
    && (!game.joinDeadlineAt || now <= game.joinDeadlineAt);
}

export function isScheduledStartReached(game, now = Date.now()) {
  return !game.scheduledStartAt || now >= game.scheduledStartAt;
}

export function resignGame(game, playerId, now = Date.now()) {
  if (game.status !== 'playing' && game.status !== 'starting') {
    throw new Error('Game is not in progress');
  }
  const color = playerColor(game, playerId);
  if (!color) {
    throw new Error('Player is not seated in this game');
  }
  return finish(
    { ...game, revision: game.revision + 1, clocks: liveClocks(game, now) },
    color === 'white' ? '0-1' : '1-0',
    'resign',
    now
  );
}

export function offerDraw(game, playerId, now = Date.now()) {
  const color = playerColor(game, playerId);
  if (game.status !== 'playing' || !color) {
    throw new Error('Only a seated player may offer a draw');
  }
  return { ...game, revision: game.revision + 1, drawOffer: color, updatedAt: now };
}

export function declineDraw(game, playerId, now = Date.now()) {
  const color = playerColor(game, playerId);
  if (!color || !game.drawOffer || game.drawOffer === color) {
    throw new Error('No opponent draw offer is pending');
  }
  return { ...game, revision: game.revision + 1, drawOffer: null, updatedAt: now };
}

export function acceptDraw(game, playerId, now = Date.now()) {
  const color = playerColor(game, playerId);
  if (!color || !game.drawOffer || game.drawOffer === color) {
    throw new Error('No opponent draw offer is pending');
  }
  return finish(
    { ...game, revision: game.revision + 1, clocks: liveClocks(game, now) },
    '½-½',
    'draw-agreed',
    now
  );
}

export function abortGame(game, playerId, now = Date.now()) {
  if (!playerColor(game, playerId)) {
    throw new Error('Player is not seated in this game');
  }
  if (game.status !== 'playing' && game.status !== 'starting') {
    throw new Error('Game is not in progress');
  }
  if (game.status === 'playing' && game.moves.length >= 2) {
    throw new Error('Games can only be aborted before move two');
  }
  return finish(
    { ...game, revision: game.revision + 1, clocks: liveClocks(game, now) },
    null,
    'abort',
    now
  );
}

export function claimFlag(game, playerId, now = Date.now()) {
  const claimant = playerColor(game, playerId);
  if (!claimant || game.status !== 'playing') {
    throw new Error('Only a seated player may claim a flag');
  }
  if (isUntimedControl(game.timeControl)) {
    throw new Error('Untimed games have no flag');
  }

  const opponent = opposite(claimant);
  const adjudicated = adjudicatedClock(game, opponent, now);
  const clocks = {
    ...game.clocks,
    [opponent]: adjudicated.remainingMs
  };
  if (game.turn !== opponent || clocks[opponent] > 0) {
    throw new Error('Opponent has not flagged');
  }

  return finish(
    { ...game, revision: game.revision + 1, clocks },
    claimant === 'white' ? '1-0' : '0-1',
    'flag',
    now
  );
}

export function abandonGame(game, playerId, now = Date.now()) {
  const color = playerColor(game, playerId);
  if (!color || game.status !== 'playing') {
    throw new Error('Only a seated player may abandon a live game');
  }
  return finish(
    { ...game, revision: game.revision + 1, clocks: liveClocks(game, now) },
    color === 'white' ? '0-1' : '1-0',
    'abandoned',
    now
  );
}

export function legalMoves(game, square) {
  if (game.status !== 'playing') {
    return [];
  }
  return legalMovesFromPosition(chessFromGame(game), square);
}

export function colorForPlayer(game, playerId) {
  return playerColor(game, playerId);
}

export function turnCode(game) {
  return chessColor(game.turn);
}
